import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebaseConfig';
import { useAuth } from './hooks/useAuth';
import LoginForm from './components/LoginForm';
import './AccountDeletionPage.css';

const AccountDeletionPage = () => {
  const { user, loading, error: authError, login, logout } = useAuth();
  const [formData, setFormData] = useState({
    reason: '',
    feedback: '',
    confirmations: {
      dataLoss: false,
      groupTransfer: false,
      permanent: false,
      finalConfirm: false
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (confirmationType) => {
    setFormData(prev => ({
      ...prev,
      confirmations: {
        ...prev.confirmations,
        [confirmationType]: !prev.confirmations[confirmationType]
      }
    }));
  };

  const isFormValid = () => {
    return (
      Object.values(formData.confirmations).every(Boolean)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      alert('Please complete all required fields and confirmations.');
      return;
    }

    setShowFinalConfirmation(true);
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Call the actual Firebase function
      const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
      const result = await deleteUserAccount({
        reason: formData.reason,
        feedback: formData.feedback
      });

      if (result.data.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Account deletion request submitted successfully. You will receive a confirmation email shortly.',
          deletionId: result.data.deletionId
        });

        // Sign out user after successful deletion request
        await logout();
      } else {
        throw new Error('Failed to submit deletion request');
      }
    } catch (error) {
      console.error('Error submitting deletion request:', error);
      setSubmitStatus({
        type: 'error',
        message: 'There was an error processing your request. Please try again later or contact support.'
      });
    } finally {
      setIsSubmitting(false);
      setShowFinalConfirmation(false);
    }
  };

  const handleCancel = () => {
    setShowFinalConfirmation(false);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="account-deletion-page">
        <div className="container">
          <div className="loading-message">Loading...</div>
        </div>
      </div>
    );
  }

  // Show login form if user is not authenticated
  if (!user) {
    return (
      <div className="account-deletion-page">
        <div className="container">
          <LoginForm onLogin={login} error={authError} loading={loading} />
        </div>
      </div>
    );
  }

  if (submitStatus?.type === 'success') {
    return (
      <div className="account-deletion-page">
        <div className="container">
          <div className="success-message">
            <div className="success-icon">✅</div>
            <h1>Account Deletion Request Submitted</h1>
            <p>{submitStatus.message}</p>
            <div className="deletion-details">
              <p><strong>Request ID:</strong> {submitStatus.deletionId}</p>
              <p><strong>Processing Time:</strong> Your account will be permanently deleted within 30 days.</p>
            </div>
            <div className="next-steps">
              <h3>What happens next:</h3>
              <ul>
                <li>You will receive a confirmation email with your request details</li>
                <li>Your account will be immediately deactivated</li>
                <li>All your data will be permanently deleted within 30 days</li>
                <li>If you own any groups, ownership will be transferred to other members or the groups will be deleted</li>
              </ul>
            </div>
            <a href="/" className="btn-primary">Return to Home</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-deletion-page">
      <div className="container">
        <div className="deletion-form-container">
          <h1>Delete Your NextLink Account</h1>
          <div className="warning-notice">
            <div className="warning-icon">⚠️</div>
            <div className="warning-content">
              <h3>This action cannot be undone</h3>
              <p>Deleting your account will permanently remove all your data, including messages, group memberships, and payment information.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="deletion-form">
            <div className="form-section">
              <h3>Logged in as</h3>
              <div className="user-info">
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Account verified:</strong> {user.emailVerified ? '✅ Yes' : '❌ No'}</p>
                <button
                  type="button"
                  onClick={logout}
                  className="btn-secondary"
                  style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                >
                  Sign out
                </button>
              </div>
            </div>

            <div className="form-section">
              <h3>Reason for Deletion (Optional)</h3>
              <div className="form-group">
                <label htmlFor="reason">Why are you deleting your account?</label>
                <select
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                >
                  <option value="">Select a reason (optional)</option>
                  <option value="no_longer_needed">No longer needed</option>
                  <option value="privacy_concerns">Privacy concerns</option>
                  <option value="switching_apps">Switching to another app</option>
                  <option value="too_many_notifications">Too many notifications</option>
                  <option value="technical_issues">Technical issues</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="feedback">Additional Feedback (Optional)</label>
                <textarea
                  id="feedback"
                  name="feedback"
                  value={formData.feedback}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Help us improve NextLink by sharing your experience..."
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Data Deletion Confirmations</h3>
              <div className="confirmations-list">
                <label className="confirmation-item">
                  <input
                    type="checkbox"
                    checked={formData.confirmations.dataLoss}
                    onChange={() => handleCheckboxChange('dataLoss')}
                  />
                  <span className="checkmark"></span>
                  I understand that all my messages, photos, and shared content will be permanently deleted
                </label>

                <label className="confirmation-item">
                  <input
                    type="checkbox"
                    checked={formData.confirmations.groupTransfer}
                    onChange={() => handleCheckboxChange('groupTransfer')}
                  />
                  <span className="checkmark"></span>
                  I understand that any groups I own will be transferred to other members or deleted if no other members exist
                </label>

                <label className="confirmation-item">
                  <input
                    type="checkbox"
                    checked={formData.confirmations.permanent}
                    onChange={() => handleCheckboxChange('permanent')}
                  />
                  <span className="checkmark"></span>
                  I understand that this action is permanent and cannot be reversed
                </label>

                <label className="confirmation-item">
                  <input
                    type="checkbox"
                    checked={formData.confirmations.finalConfirm}
                    onChange={() => handleCheckboxChange('finalConfirm')}
                  />
                  <span className="checkmark"></span>
                  I confirm that I want to permanently delete my NextLink account
                </label>
              </div>
            </div>

            <div className="form-actions">
              <a href="/privacy" className="btn-secondary">Cancel</a>
              <button
                type="submit"
                className="btn-danger"
                disabled={!isFormValid()}
              >
                Delete My Account
              </button>
            </div>
          </form>
        </div>

        {/* Final Confirmation Modal */}
        {showFinalConfirmation && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Final Confirmation</h2>
              <p>Are you absolutely sure you want to delete your NextLink account?</p>
              <div className="final-warning">
                <div className="warning-icon">🚨</div>
                <div>
                  <strong>This is your last chance to cancel.</strong>
                  <br />
                  Once confirmed, your account and all data will be permanently deleted.
                </div>
              </div>
              <div className="modal-actions">
                <button
                  onClick={handleCancel}
                  className="btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFinalSubmit}
                  className="btn-danger"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Yes, Delete My Account'}
                </button>
              </div>
            </div>
          </div>
        )}

        {submitStatus?.type === 'error' && (
          <div className="error-banner">
            <div className="error-icon">❌</div>
            <div>{submitStatus.message}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountDeletionPage;